import { NotificationsOutlined } from '@mui/icons-material';
import { Badge, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Appointment, Encounter, Patient } from 'fhir/r4b';
import { DateTime } from 'luxon';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatModal from 'src/features/chat/ChatModal';
import { IconButtonContained } from 'src/features/visits/shared/components/IconButtonContained';
import {
  getProviderNotificationSettingsForPractitioner,
  ProviderNotificationMethod,
  ProviderNotificationSettings,
} from 'utils';
import { AppointmentMessaging } from 'utils';
import { useApiClients } from '../../hooks/useAppClients';
import useEvolveUser from '../../hooks/useEvolveUser';
import { NotificationDisplay, NotificationDrawer } from './NotificationDrawer';
import {
  useGetProviderNotifications,
  useMarkReadProfessionalNotificationMutation,
  useUpdateProviderNotificationsMutation,
} from './notifications.queries';
import { useProviderNotificationsStore } from './notifications.store';

const useAppointmentMessagingById = (
  appointmentId: string | null
): { data: AppointmentMessaging | null; isLoading: boolean } => {
  const { oystehr } = useApiClients();

  return useQuery({
    queryKey: ['notification-appointment-messaging', appointmentId],
    queryFn: async (): Promise<AppointmentMessaging | null> => {
      if (!oystehr || !appointmentId) return null;

      const resources = (
        await oystehr.fhir.search<Appointment | Patient | Encounter>({
          resourceType: 'Appointment',
          params: [
            { name: '_id', value: appointmentId },
            { name: '_include', value: 'Appointment:patient' },
            { name: '_revinclude', value: 'Encounter:appointment' },
          ],
        })
      ).unbundle();

      const appointment = resources.find((r): r is Appointment => r.resourceType === 'Appointment');
      const patient = resources.find((r): r is Patient => r.resourceType === 'Patient');
      const encounter = resources.find((r): r is Encounter => r.resourceType === 'Encounter');

      if (!appointment?.id || !patient?.id || !encounter?.id) return null;

      const name = patient.name?.[0];
      const phone = patient.telecom?.find((t) => t.system === 'phone')?.value;

      return {
        id: appointment.id,
        encounterId: encounter.id,
        patient: {
          id: patient.id,
          firstName: name?.given?.[0],
          lastName: name?.family,
          dateOfBirth: patient.birthDate ?? '',
          sex: patient.gender,
          phone,
        },
        smsModel: {
          recipients: phone ? [{ recipientResourceUri: `Patient/${patient.id}`, smsNumber: phone }] : [],
          hasUnreadMessages: true,
        },
      };
    },
    enabled: !!oystehr && !!appointmentId,
  }) as { data: AppointmentMessaging | null; isLoading: boolean };
};

const NotificationChatModal: FC<{ appointmentId: string; onClose: () => void }> = ({ appointmentId, onClose }) => {
  const { data: appointment, isLoading } = useAppointmentMessagingById(appointmentId);

  if (isLoading || !appointment) return null;

  return <ChatModal appointment={appointment} onClose={onClose} onMarkAllRead={() => {}} />;
};

export const ProviderNotifications: FC = memo(() => {
  const theme = useTheme();
  const user = useEvolveUser();
  const navigate = useNavigate();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [allNotifications, setAllNotifications] = useState<NotificationDisplay[]>([]);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [localUnreadCount, setLocalUnreadCount] = useState<number>(0);
  const [chatAppointmentId, setChatAppointmentId] = useState<string | null>(null);

  const { data: notificationsData, isFetching: isFetchingQuery, refetch } = useGetProviderNotifications(nextToken);
  const updateNotifications = useUpdateProviderNotificationsMutation();
  const markReadMutation = useMarkReadProfessionalNotificationMutation();

  // Effect to handle new data from the query
  useEffect(() => {
    if (isDrawerOpen && notificationsData) {
      const newNotifications =
        notificationsData.notifications.map<NotificationDisplay>((notification) => ({
          id: notification.communication.id!,
          type: notification.communication.category?.[0]?.coding?.[0]?.code || 'system-notification',
          isRead: notification.isRead,
          message: notification.communication.payload?.[0]?.contentString || '',
          timeAgo: notification.communication.sent
            ? DateTime.fromISO(notification.communication.sent).toRelative()!
            : 'N/A',
          link: notification.appointmentID ? `/telemed/appointments/${notification.appointmentID}` : undefined,
          idAppointment: notification.appointmentID,
          title: 'Notification',
        })) || [];

      setAllNotifications((prev) => {
        if (!nextToken) return newNotifications;
        // Filter duplicates
        const existingIds = new Set(prev.map((n) => n.id));
        const uniqueNew = newNotifications.filter((n) => !existingIds.has(n.id));
        return [...prev, ...uniqueNew];
      });
      setLocalUnreadCount(notificationsData.totalUnread);
      setIsFetchingMore(false);
    } else if (!isDrawerOpen && notificationsData) {
      setLocalUnreadCount(notificationsData.totalUnread);
    }
  }, [notificationsData, nextToken, isDrawerOpen]);

  const { enabled: notificationsEnabled, method: notificationMethod }: ProviderNotificationSettings = useMemo(
    () =>
      getProviderNotificationSettingsForPractitioner(user?.profileResource) || {
        method: ProviderNotificationMethod['phone and computer'],
        enabled: false,
      },
    [user?.profileResource]
  );

  const hasMore = notificationsData?.hasMore ?? false;
  const currentNextToken = notificationsData?.nextToken;

  useEffect(() => {
    useProviderNotificationsStore.setState({ notificationsEnabled, notificationMethod: notificationMethod });
  }, [notificationsEnabled, notificationMethod]);

  const handleToggleDrawer = (): void => {
    const newState = !isDrawerOpen;
    setIsDrawerOpen(newState);
    if (newState) {
      setNextToken(undefined);
      setAllNotifications([]);
      void refetch();
    }
  };

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const handleScroll = (e: any): void => {
    const target = e.target;
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;

    if (bottom && hasMore && !isFetchingMore && !isFetchingQuery && currentNextToken) {
      setIsFetchingMore(true);
      setNextToken(currentNextToken);
    }
  };

  const handleClearAll = async (): Promise<void> => {
    if (allNotifications.length > 0) {
      // Optimistic update
      const previousNotifications = [...allNotifications];
      const previousCount = localUnreadCount;

      setAllNotifications([]);
      setLocalUnreadCount(0);
      setIsProcessing(true);

      try {
        await updateNotifications.mutateAsync({
          ids: [], // Backend handles clearing all
          status: 'completed',
        });
        setNextToken(undefined);
        void refetch();
      } catch (error) {
        console.error('Failed to clear all notifications:', error);
        setAllNotifications(previousNotifications);
        setLocalUnreadCount(previousCount);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleNotificationClick = async (notification: NotificationDisplay): Promise<void> => {
    const { type, idAppointment } = notification;

    // Handle Redirection based on type (matching patient portal pattern)
    switch (type) {
      case 'appointment-assigned':
        if (idAppointment) {
          navigate(`/telemed/appointments/${idAppointment}/visit-details`);
        } else {
          navigate('/telemed/appointments');
        }
        break;
      case 'appointment-cancelled':
        navigate('/telemed/appointments');
        break;
      case 'chat':
        if (idAppointment) {
          setChatAppointmentId(idAppointment);
        } else {
          navigate('/telemed/appointments');
        }
        break;
      default:
        if (notification.link) {
          navigate(notification.link);
        }
        break;
    }

    setIsDrawerOpen(false);

    // Mark as read if not already read
    if (!notification.isRead) {
      // Optimistic update
      const previousNotifications = [...allNotifications];
      const previousCount = localUnreadCount;

      setAllNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
      setLocalUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        await markReadMutation.mutateAsync({ id: notification.id });
        void refetch();
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        setAllNotifications(previousNotifications);
        setLocalUnreadCount(previousCount);
      }
    }
  };

  const handleArchiveNotification = async (id: string): Promise<void> => {
    try {
      // Optimistic
      setAllNotifications((prev) => prev.filter((n) => n.id !== id));
      // If it was unread, decrement count
      const wasUnread = allNotifications.find((n) => n.id === id)?.isRead === false;
      if (wasUnread) {
        setLocalUnreadCount((prev) => Math.max(0, prev - 1));
      }

      setIsProcessing(true);
      await updateNotifications.mutateAsync({
        ids: [id],
        status: 'completed',
      });
      setIsDrawerOpen(false);
      void refetch();
    } catch (error) {
      console.error('Failed to archive notification:', error);
      void refetch();
    } finally {
      setIsProcessing(false);
    }
  };

  const IconButton = (
    <IconButtonContained
      id="notifications-button"
      sx={{ marginRight: { sm: 0, md: 2 } }}
      aria-controls="notifications-drawer"
      variant="primary.lightest"
      onClick={handleToggleDrawer}
    >
      <NotificationsOutlined sx={{ color: theme.palette.primary.main }} />
    </IconButtonContained>
  );

  return (
    <>
      {localUnreadCount > 0 ? (
        <Badge
          badgeContent={localUnreadCount}
          color="warning"
          sx={{
            '& .MuiBadge-badge': {
              right: 8,
              top: 8,
            },
          }}
        >
          {IconButton}
        </Badge>
      ) : (
        IconButton
      )}
      <NotificationDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        notifications={allNotifications}
        onClear={handleClearAll}
        onArchive={handleArchiveNotification}
        onNotificationClick={handleNotificationClick}
        onScroll={handleScroll}
        hasMore={hasMore}
        loadingMore={isFetchingMore}
        isProcessing={isProcessing}
      />
      {chatAppointmentId && (
        <NotificationChatModal appointmentId={chatAppointmentId} onClose={() => setChatAppointmentId(null)} />
      )}
    </>
  );
});
